import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { PoundSterling, CreditCard, Calendar, Lock } from 'lucide-react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const AddCredit: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { balance, addCredit } = useAuth();
  const [amount, setAmount] = useState<string>('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCVC, setCardCVC] = useState('');
  const [message, setMessage] = useState('');
  const [processing, setProcessing] = useState(false);
  const stripe = useStripe();
  const elements = useElements();

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*\.?\d{0,2}$/.test(value) || value === '') {
      setAmount(value);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) {
      setMessage('Stripe has not loaded. Please try again.');
      return;
    }

    setProcessing(true);
    setMessage('');

    try {
      // Create payment intent using n8n webhook
      const response = await fetch('https://automation.simdigi.xyz/webhook/f8b646ca-73c9-4782-81ca-eaec465d00c3', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ amount: Math.round(parseFloat(amount) * 100), action: 'create_payment_intent' }),
      });

      if (!response.ok) {
        throw new Error('Failed to create payment intent');
      }

      const { clientSecret } = await response.json();
      console.log('Payment intent created:', clientSecret);

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: {
            number: cardNumber,
            exp_month: parseInt(cardExpiry.split('/')[0], 10),
            exp_year: parseInt(cardExpiry.split('/')[1], 10),
            cvc: cardCVC,
          },
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (paymentIntent.status === 'succeeded') {
        await handleSuccess(parseFloat(amount));
      } else {
        throw new Error('Payment failed');
      }
    } catch (err) {
      console.error('Payment error:', err);
      handleError(err.message || 'An error occurred during payment processing.');
    } finally {
      setProcessing(false);
    }
  };

  const handleSuccess = async (paidAmount: number) => {
    try {
      await addCredit(paidAmount);
      setMessage(`Successfully added £${paidAmount} to the account.`);
      setAmount('');
      setCardNumber('');
      setCardExpiry('');
      setCardCVC('');
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error adding credit:', error);
      setMessage('Failed to add credit to your account. Please try again.');
    }
  };

  const handleError = (errorMessage: string) => {
    console.error('Payment error:', errorMessage);
    setMessage(errorMessage);
  };

  const isValidAmount = parseFloat(amount) >= 5 && parseFloat(amount) <= 1000;

  return (
    <div className="bg-white p-4 rounded-lg shadow-lg">
      <h2 className="text-xl font-bold mb-2 text-gray-800">Add Credit</h2>
      <p className="text-sm mb-4 text-gray-600">Current Balance: £{balance}</p>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="amount" className="block text-sm font-medium text-gray-700">Amount (£)</label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <PoundSterling className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <input
              type="text"
              name="amount"
              id="amount"
              className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md"
              placeholder="0.00"
              value={amount}
              onChange={handleAmountChange}
            />
          </div>
        </div>
        <div className="mb-4">
          <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700">Card Number</label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <CreditCard className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <input
              type="text"
              id="cardNumber"
              className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
              placeholder="1234 5678 9012 3456"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value)}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="cardExpiry" className="block text-sm font-medium text-gray-700">Expiry Date</label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Calendar className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                type="text"
                id="cardExpiry"
                className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                placeholder="MM/YY"
                value={cardExpiry}
                onChange={(e) => setCardExpiry(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label htmlFor="cardCVC" className="block text-sm font-medium text-gray-700">CVC</label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                type="text"
                id="cardCVC"
                className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                placeholder="123"
                value={cardCVC}
                onChange={(e) => setCardCVC(e.target.value)}
              />
            </div>
          </div>
        </div>
        <button
          type="submit"
          disabled={!isValidAmount || processing || !stripe}
          className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
            isValidAmount && !processing && stripe
              ? 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
              : 'bg-gray-300 cursor-not-allowed'
          }`}
        >
          {processing ? 'Processing...' : 'Pay'}
        </button>
      </form>
      {message && (
        <div className={`mt-4 text-sm ${message.includes('Successfully') ? 'text-green-600' : 'text-red-600'}`}>
          {message}
        </div>
      )}
    </div>
  );
};

export default AddCredit;